import { makeAutoObservable, runInAction } from 'mobx'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { eventEmitterStore } from 'entities/EventEmitter'
import { type Contact } from 'entities/Contacts/model/Contact'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  ContactsDetailsStore,
  type IContactsDetailsManageStoreConfig,
  type IContactsDetailsStoreConfig,
  type IOpenContactsDetailsData,
  type IOpenContactsDetailsSource,
  type IOpenConversationsDetailsData,
} from 'widgets/ContactsDetails'

export class ContactsDetailsManageStore {
  private _contactsDetailsStore: ContactsDetailsStore | null = null
  private _disabled = false
  private _config: IContactsDetailsManageStoreConfig | null = null

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  setConfig = (config: IContactsDetailsManageStoreConfig) => {
    this._config = {
      ...this._config,
      ...config,
    }
  }

  init = () => {
    this.initEvents()
  }

  reset = () => {
    this.resetContactsDetails()
    eventEmitterStore.unsubscribe('deleteContactsDetails', this.onManageContactsDetails)
  }

  resetContactsDetails = () => {
    this._contactsDetailsStore?.dispose()
    this._contactsDetailsStore = null
  }

  initEvents = () => {
    eventEmitterStore.subscribe('deleteContactsDetails', this.onManageContactsDetails)
  }

  setContactsDetails = (config: IContactsDetailsStoreConfig) => {
    if (this._contactsDetailsStore) {
      this._contactsDetailsStore.dispose()
      this._contactsDetailsStore.initReactions()
      this._contactsDetailsStore.setConfig(config)

      return
    }

    this._contactsDetailsStore = new ContactsDetailsStore(config, this._entities)
  }

  onManageContactsDetails = () => {
    if (this._contactsDetailsStore?.isVariantConversations) {
      this._contactsDetailsStore?.contactsDetailsGlobalStore.handleChangeCollapse(true)
      return
    }
    this.onDeleteContactsDetails()
  }

  onDeleteContactsDetails = () => {
    this._contactsDetailsStore?.dispose()
    this._contactsDetailsStore?.contactsDetailsGlobalStore.dispose()
    this._contactsDetailsStore = null
  }

  setDisabled = (value: boolean) => {
    this._disabled = value
  }

  existContact = async (contactId?: number, conversationId?: number) => {
    try {
      if (!contactId && conversationId) {
        const conversation = await this._entities.conversationStore.getById({
          id: conversationId,
        })

        contactId = conversation?.contact_id
      }

      const contact = contactId
        ? await this._entities.contactsStore.getById(contactId, true)
        : undefined

      return contact
    } catch (e) {
      console.log(e)
    }
  }

  // Conversations Call History
  onOpenConversationDetails = async (
    data: IOpenConversationsDetailsData,
    source: IOpenContactsDetailsSource
  ) => {
    runInAction(() => {
      this._disabled = true
    })

    await this._entities.usersStore.initPromise

    const contact = await this.existContact(data.contactId, data.conversationId)

    if (
      !contact ||
      !this._entities.inboxesStore.sharedInboxes.length ||
      this._entities.usersStore.user?.isShouldRequestAccess
    ) {
      modalStore.addModal({
        disabledOnAllClose: true,
        type: ModalTypeList.INFO,
        title: 'Content unavailable',
        id: 'content_unavailable',
        desc: "We're sorry, but you don't currently have access to this content or it was deleted. Please contact the user who shared the link with you to request access.",
      })

      runInAction(() => {
        this._disabled = false
      })

      return
    }

    const contactId = data.contactId
    const inboxId = data.inboxId
    const conversationId = data.conversationId

    this._entities.eventLogStore.logEvent(
      'ConvSlideout Opened',
      {
        event_id: 'convslideout_opened',
        from: source,
      },
      {
        groupId: this._entities.organizationStore.id,
      }
    )

    const sub_source = `conv_slideout_${source.replace(' ', '_')}`

    this._contactsDetailsStore = new ContactsDetailsStore(
      {
        conversationId: conversationId,
        contactId: contactId,
        variant: 'open_conversation',
        profileCardProps: {
          onClickCall: () => {
            this._entities.eventLogStore.logEvent(
              'ConvSlideout Call Made',
              {
                event_id: 'convslideout_call_made',
                from: source,
              },
              {
                groupId: this._entities.organizationStore.id,
              }
            )
          },
          enableCall: true,
        },
        onDeleteContactsDetails: this.onDeleteContactsDetails,
        emitOpenConversationSignal: () => {
          this._config?.emitOpenConversationSignal?.()
        },
        onCloseContactDetails: this.onDeleteContactsDetails,
        sub_source: sub_source,
        headerProps: {
          iconHide: 'close',
          textHide: 'Close details',
        },
        notesProps: {
          hideMessageFieldActions: ['ai_assistant'],
        },
      },
      this._entities
    )

    if (conversationId) {
      this._contactsDetailsStore.contactsConversationOpenStore?.setConversationId(conversationId)
    }

    if (inboxId) {
      this._contactsDetailsStore.contactsConversationOpenStore?.open(inboxId)
    }

    runInAction(() => {
      this._disabled = false
    })
  }

  // Contacts Table
  onOpenContactDetails = async (
    data: IOpenContactsDetailsData,
    source: IOpenContactsDetailsSource,
    checkInbox?: boolean,
    callback?: (contact: Contact) => void
  ) => {
    runInAction(() => {
      this._disabled = true
    })

    await Promise.all([
      this._entities.usersStore.initPromise,
      this._entities.inboxesStore.fetchInboxes(),
    ])
    const contact = await this.existContact(data.contactId)

    if (
      !contact ||
      (checkInbox && !data.inboxId) ||
      !this._entities.inboxesStore.sharedInboxes.length ||
      this._entities.usersStore.user?.isShouldRequestAccess
    ) {
      modalStore.addModal({
        disabledOnAllClose: true,
        type: ModalTypeList.INFO,
        title: 'Content unavailable',
        id: 'content_unavailable',
        desc: "We're sorry, but you don't currently have access to this content or it was deleted. Please contact the user who shared the link with you to request access.",
      })

      runInAction(() => {
        this._disabled = false
      })

      return
    }

    const contactId = data.contactId
    const inboxId = data.inboxId
    const messageId = data.messageId

    this._entities.eventLogStore.logEvent(
      'ConvSlideout Opened',
      {
        event_id: 'convslideout_opened',
        from: source,
      },
      {
        groupId: this._entities.organizationStore.id,
      }
    )

    const sub_source = `conv_slideout_${source.replace(' ', '_')}`

    runInAction(() => {
      this._contactsDetailsStore = new ContactsDetailsStore(
        {
          contactId: contactId,
          inboxId: inboxId,
          messageId,
          variant: 'open_conversation',
          profileCardProps: {
            onClickCall: () => {
              this._entities.eventLogStore.logEvent(
                'ConvSlideout Call Made',
                {
                  event_id: 'convslideout_call_made',
                  from: source,
                },
                {
                  groupId: this._entities.organizationStore.id,
                }
              )
            },
            enableCall: true,
          },
          onDeleteContactsDetails: this.onDeleteContactsDetails,
          onCloseContactDetails: this.onDeleteContactsDetails,
          onUpdateContact: callback,
          sub_source: sub_source,
          headerProps: {
            iconHide: 'close',
            textHide: 'Close details',
          },
          notesProps: {
            hideMessageFieldActions: ['ai_assistant'],
          },
        },
        this._entities
      )

      this._contactsDetailsStore.contactsConversationOpenStore?.open()

      this._disabled = false
    })
  }

  get contactsDetailsStore() {
    return this._contactsDetailsStore
  }

  get disabled() {
    return this._disabled
  }
}
